/**
 * SpeedThresholdEditor - UI for configuring speed-based movement evaluation
 *
 * Allows coaches to define speed requirements:
 * - Same speed: Within tolerance of template speed
 * - Faster or equal: At least as fast as template
 * - Slower or equal: No faster than template
 *
 * Amber accent color scheme for visual distinction from
 * distance (purple) and movement (green) controls.
 */

import React, { useCallback } from 'react';
import { BoltIcon } from '@heroicons/react/24/solid';
import { cn } from '@/utils/cn';
import { Tooltip } from '@/components/ui';
import type {
  SpeedThreshold,
  SpeedComparisonOperator,
} from '@/types/trajectory';
import {
  SPEED_COMPARISON_LABELS,
  SPEED_COMPARISON_DESCRIPTIONS,
  DEFAULT_SPEED_THRESHOLD,
} from '@/types/trajectory';

// =============================================================================
// Constants (defined outside component for referential stability)
// =============================================================================

const MODE_OPTIONS: ReadonlyArray<{ value: SpeedComparisonOperator; icon: string }> = [
  { value: 'same_as', icon: '≈' },
  { value: 'faster_or_equal', icon: '≥' },
  { value: 'slower_or_equal', icon: '≤' },
] as const;

// =============================================================================
// Types
// =============================================================================

interface SpeedThresholdEditorProps {
  value: SpeedThreshold | undefined;
  onChange: (value: SpeedThreshold | undefined) => void;
  className?: string;
  showDescription?: boolean;
}

// =============================================================================
// Comparison Mode Button
// =============================================================================

interface ModeButtonProps {
  mode: SpeedComparisonOperator;
  icon: string;
  label: string;
  description: string;
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const ModeButton: React.FC<ModeButtonProps> = ({
  mode: _mode,
  icon,
  label,
  description,
  isSelected,
  onClick,
  disabled = false,
}) => (
  <Tooltip content={description}>
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex-1 flex flex-col items-center justify-center',
        'px-3 py-2.5 rounded-lg transition-all duration-200',
        'border-2 font-medium text-sm',
        'focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:ring-offset-1',
        isSelected
          ? 'bg-amber-500/15 border-amber-500/50 text-amber-400 shadow-sm'
          : 'bg-dark-card border-border-subtle text-text-secondary hover:border-amber-500/30 hover:bg-amber-500/10',
        disabled && 'opacity-50 cursor-not-allowed hover:border-border-subtle hover:bg-dark-card'
      )}
    >
      <span className={cn(
        'text-lg mb-0.5 transition-transform duration-200',
        isSelected && 'scale-110'
      )}>
        {icon}
      </span>
      <span className="whitespace-nowrap">{label}</span>
    </button>
  </Tooltip>
);

// =============================================================================
// Range Slider with Amber Styling
// =============================================================================

interface ToleranceSliderProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

const ToleranceSlider: React.FC<ToleranceSliderProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const minBound = 100 - value;
  const maxBound = 100 + value;

  return (
    <div className={cn(
      'space-y-2 transition-opacity duration-200',
      disabled && 'opacity-50'
    )}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-text-primary">
          Speed Tolerance
        </label>
        <span className="text-sm font-semibold text-amber-400 tabular-nums">
          ±{value}%
        </span>
      </div>

      {/* Custom styled range input */}
      <div className="relative">
        <input
          type="range"
          min={5}
          max={50}
          step={5}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          disabled={disabled}
          className={cn(
            'w-full h-2 rounded-full appearance-none cursor-pointer',
            'bg-gradient-to-r from-amber-100 via-amber-200 to-amber-100',
            '[&::-webkit-slider-thumb]:appearance-none',
            '[&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5',
            '[&::-webkit-slider-thumb]:rounded-full',
            '[&::-webkit-slider-thumb]:bg-gradient-to-b [&::-webkit-slider-thumb]:from-amber-400 [&::-webkit-slider-thumb]:to-amber-500',
            '[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-amber-600',
            '[&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer',
            '[&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-150',
            '[&::-webkit-slider-thumb]:hover:scale-110',
            '[&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5',
            '[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0',
            '[&::-moz-range-thumb]:bg-gradient-to-b [&::-moz-range-thumb]:from-amber-400 [&::-moz-range-thumb]:to-amber-500',
            disabled && 'cursor-not-allowed'
          )}
        />

        {/* Tick marks */}
        <div className="flex justify-between px-1 mt-1">
          {[5, 15, 25, 35, 45].map((tick) => (
            <div
              key={tick}
              className={cn(
                'w-0.5 h-1.5 rounded-full transition-colors duration-200',
                tick <= value ? 'bg-amber-400' : 'bg-border-subtle'
              )}
            />
          ))}
        </div>
      </div>

      {/* Range preview */}
      <div className="flex items-center justify-center gap-2 text-xs text-text-secondary bg-dark-elevated-1 rounded-md py-1.5 px-3">
        <span className="font-medium text-text-secondary">Athlete speed:</span>
        <span className="tabular-nums font-semibold text-amber-400">
          {minBound}% – {maxBound}%
        </span>
        <span>of template</span>
      </div>
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

const SpeedThresholdEditor: React.FC<SpeedThresholdEditorProps> = ({
  value,
  onChange,
  className,
  showDescription = true,
}) => {
  // Derive state from props
  const isEnabled = value?.enabled ?? false;
  const currentComparison = value?.comparison ?? 'same_as';
  const currentTolerance = value?.tolerance_percent ?? 15;

  // Handle enable/disable toggle
  const handleToggle = useCallback(() => {
    if (isEnabled) {
      // Disable - return undefined
      onChange(undefined);
    } else {
      // Enable with defaults
      onChange({
        ...DEFAULT_SPEED_THRESHOLD,
        enabled: true,
      });
    }
  }, [isEnabled, onChange]);

  // Handle comparison mode change - spread existing value to preserve other fields
  const handleComparisonChange = useCallback((comparison: SpeedComparisonOperator) => {
    if (!value?.enabled) return; // Guard: only change if enabled
    onChange({
      ...value,
      comparison,
    });
  }, [value, onChange]);

  // Handle tolerance change - spread existing value to preserve other fields
  const handleToleranceChange = useCallback((tolerance_percent: number) => {
    if (!value?.enabled) return; // Guard: only change if enabled
    onChange({
      ...value,
      tolerance_percent,
    });
  }, [value, onChange]);

  return (
    <div
      className={cn(
        'rounded-xl border-2 transition-all duration-300',
        isEnabled
          ? 'border-amber-500/40 bg-amber-500/5 shadow-sm'
          : 'border-border-subtle bg-dark-card',
        className
      )}
    >
      {/* Header */}
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-3 cursor-pointer select-none',
          'transition-colors duration-200',
          isEnabled ? 'hover:bg-amber-500/10' : 'hover:bg-dark-elevated-1'
        )}
        onClick={handleToggle}
      >
        {/* Checkbox */}
        <div className="relative">
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={handleToggle}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'h-5 w-5 rounded border-2 cursor-pointer appearance-none',
              'transition-all duration-200',
              'focus:ring-2 focus:ring-amber-400/50 focus:ring-offset-1',
              isEnabled
                ? 'bg-amber-500 border-amber-600'
                : 'bg-dark-elevated-1 border-border-strong hover:border-amber-500'
            )}
          />
          {isEnabled && (
            <svg
              className="absolute inset-0 w-5 h-5 text-white pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>

        {/* Icon and label */}
        <div className={cn(
          'flex items-center gap-2 transition-colors duration-200',
          isEnabled ? 'text-amber-400' : 'text-text-secondary'
        )}>
          <BoltIcon className={cn(
            'w-5 h-5 transition-all duration-300',
            isEnabled && 'text-amber-500 drop-shadow-sm'
          )} />
          <span className="font-semibold text-sm">
            Speed Requirement
          </span>
        </div>

        {/* Status indicator */}
        {isEnabled && (
          <span className="ml-auto text-xs font-medium text-amber-400 bg-amber-500/15 px-2 py-0.5 rounded-full">
            Active
          </span>
        )}
      </div>

      {/* Description */}
      {showDescription && (
        <p className={cn(
          'px-4 pb-2 text-xs transition-colors duration-200',
          isEnabled ? 'text-amber-400/80' : 'text-text-muted'
        )}>
          Compare athlete movement speed to template
        </p>
      )}

      {/* Expanded content when enabled */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-out',
          isEnabled ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="px-4 pb-4 space-y-4">
          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-amber-200 to-transparent" />

          {/* Comparison mode buttons */}
          <div>
            <label className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-2 block">
              Speed Comparison
            </label>
            <div className="flex gap-2">
              {MODE_OPTIONS.map(({ value: mode, icon }) => (
                <ModeButton
                  key={mode}
                  mode={mode}
                  icon={icon}
                  label={SPEED_COMPARISON_LABELS[mode]}
                  description={SPEED_COMPARISON_DESCRIPTIONS[mode]}
                  isSelected={currentComparison === mode}
                  onClick={() => handleComparisonChange(mode)}
                />
              ))}
            </div>
          </div>

          {/* Tolerance slider (only for same_as mode) */}
          {currentComparison === 'same_as' && (
            <ToleranceSlider
              value={currentTolerance}
              onChange={handleToleranceChange}
            />
          )}

          {/* Summary for non-tolerance modes */}
          {currentComparison !== 'same_as' && (
            <div className="flex items-center gap-2 text-xs text-text-secondary bg-dark-elevated-1 rounded-md py-2 px-3">
              <BoltIcon className="w-4 h-4 text-amber-500" />
              <span>
                {currentComparison === 'faster_or_equal'
                  ? 'Athlete must move at least as fast as the template'
                  : 'Athlete must not move faster than the template'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpeedThresholdEditor;
